/**
 * Settings — the account, what's been spent, and the brand mark.
 *
 * There is no API-key field here: the image-service key lives server-side and
 * never reaches the browser. There's nothing for a user to paste.
 */
import { useAuth } from '../lib/auth';
import { money } from '../lib/compose';
import BrandMark, { Wordmark } from '../components/BrandMark';

export interface SettingsProps {
  spend: { day: string; total: number; images: number }[];
}

export default function Settings({ spend }: SettingsProps) {
  const { user, signOut } = useAuth();
  const total = spend.reduce((sum, d) => sum + d.total, 0);
  const images = spend.reduce((sum, d) => sum + d.images, 0);

  return (
    <div className="pad">
      <div className="page-head">
        <div>
          <h2>Settings</h2>
          <p>Your account, and what the last month cost.</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 13, maxWidth: 940, alignItems: 'start' }}>
        <div className="card">
          <span className="eyebrow">Account</span>
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '9px 13px', marginTop: 13, alignItems: 'center' }}>
            <span className="faint" style={{ fontSize: 12 }}>Signed in</span>
            <span style={{ fontSize: 13 }}>{user?.email ?? '—'}</span>
            <span className="faint" style={{ fontSize: 12 }}>Account</span>
            <span className="mono faint" style={{ fontSize: 11 }}>one Fluid account, every app</span>
          </div>
          <button className="btn btn-sm" style={{ marginTop: 16 }} onClick={() => void signOut()}>Sign out</button>
        </div>

        <div className="card">
          <span className="eyebrow">Last 30 days</span>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '12px 0 4px' }}>
            <span className="mono" style={{ fontSize: 26 }}>{money(total)}</span>
            <span className="faint" style={{ fontSize: 12.5 }}>{images} image{images === 1 ? '' : 's'}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '5px 12px', marginTop: 14 }}>
            {spend.slice(0, 8).map((d) => (
              <div key={d.day} style={{ display: 'contents' }}>
                <span className="mono faint" style={{ fontSize: 11 }}>{d.day}</span>
                <span className="mono faint" style={{ fontSize: 11 }}>{d.images}×</span>
                <span className="mono" style={{ fontSize: 11 }}>{money(d.total)}</span>
              </div>
            ))}
            {spend.length === 0 && <span className="faint" style={{ fontSize: 12, gridColumn: '1 / -1' }}>Nothing yet.</span>}
          </div>
          <p className="faint" style={{ fontSize: 11.5, marginTop: 14, lineHeight: 1.55 }}>
            Billed by the image at cost — $0.045 at 1K, $0.090 at 2K, plus $0.003 for each input past
            the first.
          </p>
        </div>

        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <span className="eyebrow">Brand mark</span>
          <p className="faint" style={{ fontSize: 12, margin: '7px 0 16px', lineHeight: 1.55 }}>
            The same drop as every other Fluid product, the same diagonal gradient, and a white glyph
            inside saying which one it is — FluidTalk's three dots, FluidCloud's cloud, FluidDream's star.
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 26, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18 }}>
              <BrandMark size={64} />
              <BrandMark size={32} />
              <BrandMark size={20} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              <Wordmark size={22} />
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px' }}>
                <span className="faint" style={{ fontSize: 12 }}>Gradient</span>
                <span className="mono" style={{ fontSize: 11.5 }}>#ffb066 → #e05c1f</span>
                <span className="faint" style={{ fontSize: 12 }}>Accent</span>
                <span className="mono" style={{ fontSize: 11.5 }}>#ef7134</span>
                <span className="faint" style={{ fontSize: 12 }}>Drop path</span>
                <span className="mono faint" style={{ fontSize: 11.5 }}>shared, byte-identical</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
